import type { Arg, ArgsOf, ArgsParam, Catalog, Entry, Message, MessageKey } from './catalog';
import type { Locale } from './locale';
import { de } from './messages/de';
import { en } from './messages/en';

/**
 * **Formatting** (docs/I18N.md): a `Message` in, a string out, in one locale.
 * Pure and total: an unknown key formats as the key itself and a missing
 * argument leaves its `{slot}` in place, so a stored descriptor from an older
 * catalog never throws. The language work is the platform's: plurals through
 * `Intl.PluralRules`, lists through `Intl.ListFormat`, numbers through
 * `Intl.NumberFormat`.
 *
 * A sentence is one key. Calling code never joins two formatted fragments into
 * a sentence; a part that varies is an argument, and a part that is itself a
 * sentence fragment with its own words is a nested `Message`.
 */

const CATALOGS: { readonly [L in Locale]: Catalog } = { en, de };

/** Build a descriptor. The key and its arguments are checked at the call site:
 *  `m('resolution.statement.moved', { stage })`. */
export function m<K extends MessageKey>(key: K, ...args: ArgsParam<K>): Message {
  const [given] = args;
  return given === undefined ? { key } : { key, args: given as Message['args'] };
}

/** Whether a string is a key of the catalog: for a key built from data (a
 *  module's field key), which the type system cannot check. */
export function isMessageKey(key: string): key is MessageKey {
  return Object.hasOwn(en, key);
}

/** A string shown as it is, in every locale: text that has no key yet (a
 *  stored sentence the legacy parser does not recognise, an error that carries
 *  no `Message`). */
export function verbatim(text: string): Message {
  return m('common.verbatim.text', { text });
}

/**
 * Sentences side by side, each its own key (a sub-line that says what stays
 * and why, one sentence per fact). The catalog holds the space between them
 * (`common.sentences.two`), so calling code never joins formatted text.
 * `undefined` for none.
 */
export function sentences(parts: readonly Message[]): Message | undefined {
  if (parts.length === 0) return undefined;
  return parts.reduceRight((second, first) => m('common.sentences.two', { first, second }));
}

/** Format one descriptor in one locale. */
export function format(locale: Locale, message: Message): string {
  return formatParts<never>(locale, message.key, message.args ?? {}).join('');
}

/**
 * Format into parts, for a surface that puts an element inside a sentence (a
 * link, a name in bold): a slot whose value is not an `Arg` is passed through
 * untouched, and the caller renders the parts in order.
 */
export function formatParts<N>(
  locale: Locale,
  key: string,
  args: { readonly [name: string]: Arg | N },
): (string | N)[] {
  const entry = (CATALOGS[locale] as Record<string, Entry | undefined>)[key];
  if (entry === undefined) return [key];
  return formatEntry(locale, entry, args);
}

/** One entry, outside any catalog: the step `formatParts` runs once it has
 *  found the key. Exported for the catalog's own tests. */
export function formatEntry<N>(
  locale: Locale,
  entry: Entry,
  args: { readonly [name: string]: Arg | N },
): (string | N)[] {
  const template = typeof entry === 'string' ? entry : pluralVariant(locale, entry, args['count']);
  const out: (string | N)[] = [];
  let last = 0;
  for (const match of template.matchAll(SLOT)) {
    const name = match[1]!;
    if (!(name in args)) continue;
    if (match.index > last) out.push(template.slice(last, match.index));
    const value = args[name] as Arg | N;
    out.push(isArg(value) ? formatArg(locale, value) : value);
    last = match.index + match[0].length;
  }
  if (last < template.length) out.push(template.slice(last));
  return mergeStrings(out);
}

const SLOT = /\{([A-Za-z][A-Za-z0-9]*)\}/g;

function pluralVariant(locale: Locale, entry: Exclude<Entry, string>, count: unknown): string {
  if (typeof count !== 'number') return entry.other;
  return entry[pluralRules(locale).select(count)] ?? entry.other;
}

function formatArg(locale: Locale, value: Arg): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return numberFormat(locale).format(value);
  if (isMessageValue(value)) return format(locale, value);
  return listFormat(locale).format(
    value.map((item) => (typeof item === 'string' ? item : format(locale, item))),
  );
}

function isArg(value: unknown): value is Arg {
  if (typeof value === 'string' || typeof value === 'number') return true;
  if (Array.isArray(value)) return value.every((v) => typeof v === 'string' || isMessageValue(v));
  return isMessageValue(value);
}

/** A plain descriptor: an object with a string `key` and nothing besides an
 *  optional `args`. A rendered element (which also carries a `key`) is not one. */
function isMessageValue(value: unknown): value is Message {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record['key'] === 'string' &&
    Object.keys(record).every((k) => k === 'key' || k === 'args')
  );
}

function mergeStrings<N>(parts: (string | N)[]): (string | N)[] {
  const out: (string | N)[] = [];
  for (const part of parts) {
    const prev = out[out.length - 1];
    if (typeof part === 'string' && typeof prev === 'string') out[out.length - 1] = prev + part;
    else out.push(part);
  }
  return out;
}

// The Intl objects, one per locale, built on first use.
const plurals = new Map<Locale, Intl.PluralRules>();
const numbers = new Map<Locale, Intl.NumberFormat>();
const lists = new Map<Locale, Intl.ListFormat>();

function pluralRules(locale: Locale): Intl.PluralRules {
  let rules = plurals.get(locale);
  if (!rules) plurals.set(locale, (rules = new Intl.PluralRules(locale)));
  return rules;
}

/** `min2` groups from five digits on (10,000 / 10.000), so a four-digit count
 *  reads the same in both locales. */
function numberFormat(locale: Locale): Intl.NumberFormat {
  let nf = numbers.get(locale);
  if (!nf) numbers.set(locale, (nf = new Intl.NumberFormat(locale, { useGrouping: 'min2' })));
  return nf;
}

function listFormat(locale: Locale): Intl.ListFormat {
  let lf = lists.get(locale);
  if (!lf) lists.set(locale, (lf = new Intl.ListFormat(locale, { type: 'conjunction' })));
  return lf;
}

/**
 * **The two call forms share one shape** (docs/I18N.md): a server component
 * gets a `Translator` from the viewer's locale (`getT()`, src/lib/viewer.ts), a
 * client component from `useT()` (src/lib/locale-context.tsx). Both are this.
 */
export interface Translator {
  /** A sentence by key: `t('kit.select.none')`, `t('kit.relativeTime.hours', { count })`. */
  <K extends MessageKey>(key: K, ...args: ArgsParam<K>): string;
  readonly locale: Locale;
  /** A descriptor a domain function returned. */
  msg(message: Message): string;
  /** A sentence with an element inside it; see `formatParts`. */
  rich<K extends MessageKey, N>(key: K, args: RichArgs<K, N>): (string | N)[];
}

/** The arguments of a rich call: each slot takes its usual `Arg` or an element. */
export type RichArgs<K extends MessageKey, N> = {
  readonly [S in keyof ArgsOf<K>]: ArgsOf<K>[S] | N;
};

const translators = new Map<Locale, Translator>();

/** The translator for one locale (one instance per locale, so it is stable
 *  across renders). */
export function translator(locale: Locale): Translator {
  const held = translators.get(locale);
  if (held) return held;
  const t = (<K extends MessageKey>(key: K, ...args: ArgsParam<K>) =>
    format(locale, m(key, ...args))) as Translator;
  Object.assign(t, {
    locale,
    msg: (message: Message) => format(locale, message),
    rich: <K extends MessageKey, N>(key: K, args: RichArgs<K, N>) =>
      formatParts<N>(locale, key, args as { readonly [name: string]: Arg | N }),
  });
  translators.set(locale, t);
  return t;
}
